import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import SectionHeader from '../../../util/component/SectionHeader';
import {Box, Button, Grid, Paper, useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {GlobalContext} from '../../../util/state/GlobalContextProvider';
import * as yup from 'yup';
import {FormProvider, useForm} from 'react-hook-form';
import ProfileData from '../component/ProfileData';
import CustomContentWrapper from '../../../util/component/CustomContentWrapper';
import {yupResolver} from "@hookform/resolvers/yup";
import axios from "axios";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {useMutation, useQuery} from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {profileDetailStructure} from "../state/profileState";
import {useParams} from "react-router-dom";
import {useHasScope} from "../../../config/globalConfig";
import NotAuthorizedDialog from "../../authentication/component/NotAuthorizedDialog";

interface ProfileUpdateProps {
    name: string;
    legalEntity: any;
    department: any;
    auths: any[];
}

const ProfileUpdate = () => {
    const hasScope = useHasScope;
    const {id} = useParams()
    const [currentProfile, setCurrentProfile] = useState<any>(null);
    const theme = useTheme();
    const {t} = useTranslation(['operator']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities);
    const [departments] = useState<any[]>(globalData.user?.departments);
    const [profiles, setProfiles] = useState<any[]>([]);
    const setProfileDetailStructure = useSetRecoilState(profileDetailStructure);
    const {profileDtos} = useRecoilValue(profileDetailStructure);

    useEffect(() => {
        setProfiles(profileDtos);
    }, [profileDtos]);

    useEffect(() => {
        axios.post('/profile/find/' + id).then((response) => {
            setCurrentProfile(response.data);
        }).catch((error) => {
            console.log(error);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.tryLater'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });

    }, [id]);

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .trim()
            .required('message.validation.fieldRequired'),
        legalEntity: yup
            .object()
            .required('message.validation.fieldRequired'),
        department: yup
            .object()
            .required('message.validation.fieldRequired'),
    });

    const methods = useForm<ProfileUpdateProps>({
        resolver: yupResolver(validationSchema),
        defaultValues: useMemo(() => ({
            name: currentProfile?.name,
            legalEntity: currentProfile?.legalEntity,
            department: currentProfile?.department,
            auths: currentProfile?.auths,
        }), [currentProfile])
    });

    const {reset, handleSubmit} = methods;

    useEffect(() => {
        reset(currentProfile)
    }, [currentProfile]);

    const updateOnSuccess = useCallback((response: any) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('translation:success.updateOperation'),
                    type: snackbarTypes.success,
                    show: true,
                    autoClose: true,
                },
            },
        });
    }, [dispatchGlobalData, t]);

    const updateOnError = useCallback((error: any) => {
        console.log(error);
        if (error.response.status === 400) {
            let errorMsg = t('translation:error.badRequest');
            JSON.parse(error.response.data.errorDetails).forEach((e: string) => {
                errorMsg = errorMsg.concat('\n').concat(t('translation:error.'.concat(e)));
            });
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: errorMsg,
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('translation:error.updateOperation'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }
    }, [dispatchGlobalData, t]);

    const updateProfile = useMutation({
        mutationFn: (params: any) => {
            params.auths = params.auths.filter((auth: any) => !!auth.id);
            return axios({
                method: 'put',
                url: '/profile',
                data: params,
            })
        },
        onSuccess: updateOnSuccess,
        onError: updateOnError
    })
    const findProfileDetailStructure = () => {
        return axios.get('/profile/structure')
    }

    const onSuccess = (response: any) => {
        setProfileDetailStructure(response.data);
    }
    const onError = (error: any) => {
        console.log(error);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('message.error.tryLater'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    const {isFetching} = useQuery(['findProfileDetailStructure'], findProfileDetailStructure, {
        onSuccess,
        onError
    })

    return (

        <Box>
            <SectionHeader
                title={t('label.updateProfile')}
            />
            <CustomContentWrapper>
                {hasScope('SCOPE_VIEW_PROFILE') ? <FormProvider {...methods}>
                    <form onSubmit={handleSubmit((params: any) => updateProfile.mutate(params))}>
                        <Paper style={{
                            borderRadius: "4px",
                            padding: theme.spacing(1) + ' ' + theme.spacing(2),
                            marginBottom: theme.spacing(2)
                        }}
                               elevation={1}
                        >
                            <Grid container spacing={2}>
                                <ProfileData
                                    legalEntities={legalEntities}
                                    departments={departments}
                                    profiles={profiles}
                                />
                                {hasScope('SCOPE_EDIT_PROFILE') && <Grid item xs={12}>
                                    <Box mt={2}>
                                        <Button
                                            type="submit"
                                            fullWidth
                                        >
                                            {t('action.save')}
                                        </Button>
                                    </Box>
                                </Grid>}
                            </Grid>
                        </Paper>
                    </form>
                </FormProvider> : <NotAuthorizedDialog/>}
            </CustomContentWrapper>
            <CustomLoader loading={isFetching || updateProfile.isLoading}/>
        </Box>

    );
};

export default ProfileUpdate;

